// ==========================================================================
// Generated Link Capture
// ==========================================================================
// Runs once on app boot. If the URL carries `?link=<slug>`, persist it to
// localStorage, fire-and-forget log the click to the backend, then strip the
// param from the address bar via history.replaceState so the user never sees
// it after the redirect.
//
// Safe to call multiple times — only acts when `?link=` is present.
//
// If the user is already authenticated on this device, the click is treated
// as engagement (existing user revisiting), not acquisition: we still strip
// the param from the URL for tidiness, but we do not POST a click row and we
// do not overwrite any stored slug. This keeps clicks_count and the
// conversion-rate column honest — repeat clicks by an already-converted
// user shouldn't deflate the funnel math.

import { recordGeneratedLinkClick } from '@/services/generatedLink.service';
import { setGeneratedLinkToken } from '@/lib/generatedLinkStorage';

const QUERY_PARAM = 'link';
const AUTH_TOKEN_KEY = 'access_token';

function isAuthenticated(): boolean {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) !== null;
  } catch {
    return false;
  }
}

function stripLinkParamFromUrl(url: URL): void {
  url.searchParams.delete(QUERY_PARAM);
  // url.search is already '' when no params remain, or '?key=val…' otherwise —
  // no need to reimplement that conditional.
  const cleanUrl = url.pathname + url.search + url.hash;
  window.history.replaceState(window.history.state, '', cleanUrl);
}

export function captureGeneratedLink(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  const slug = url.searchParams.get(QUERY_PARAM);

  if (!slug) {
    return;
  }

  if (isAuthenticated()) {
    // Existing user re-clicking the link on this device — treat as
    // engagement, not acquisition. Strip the param and bail.
    stripLinkParamFromUrl(url);
    return;
  }

  setGeneratedLinkToken(slug);
  void recordGeneratedLinkClick(slug);
  stripLinkParamFromUrl(url);
}
